/**
 * TypewriterPlaceholder Component
 *
 * Renders animated placeholder text with a typewriter effect.
 * Shows a blinking cursor during animation and smoothly transitions
 * between placeholder states using backspace deletion and typing.
 */

import { cn } from '@/lib/cn';
import * as m from 'motion/react-m';
import { memo } from 'react';
import {
  useTypewriterPlaceholder,
  type TypewriterPhase,
} from '../hooks/useTypewriterPlaceholder';

interface TypewriterPlaceholderProps {
  /** The target placeholder text to display */
  placeholder: string;
  /** Additional class names for the container */
  className?: string;
  /** Whether animation is enabled (default: true) */
  enabled?: boolean;
  /** Speed of deletion in ms per character (default: 20ms) */
  deleteSpeed?: number;
  /** Speed of typing in ms per character (default: 30ms) */
  typeSpeed?: number;
}

/**
 * Blinking cursor component shown during animation
 */
const BlinkingCursor = ({ phase }: { phase: TypewriterPhase }) => {
  // Only show cursor during animation
  if (phase === 'idle') {
    return null;
  }

  return (
    <m.span
      initial={{ opacity: 1 }}
      animate={{ opacity: [1, 0] }}
      transition={{
        duration: 0.5,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'linear',
      }}
      className="inline-block h-[1.1em] w-px bg-text-secondary align-middle"
      aria-hidden="true"
    />
  );
};

/**
 * TypewriterPlaceholder - Animated placeholder with typewriter effect
 *
 * @example
 * ```tsx
 * <TypewriterPlaceholder
 *   placeholder="Search for something..."
 *   className="text-text-secondary"
 * />
 * ```
 */
export const TypewriterPlaceholder = memo(function TypewriterPlaceholder({
  placeholder,
  className,
  enabled = true,
  deleteSpeed = 20,
  typeSpeed = 30,
}: TypewriterPlaceholderProps) {
  const { displayedText, phase } = useTypewriterPlaceholder({
    placeholder,
    enabled,
    deleteSpeed,
    typeSpeed,
  });

  return (
    <span
      className={cn('pointer-events-none select-none text-text-secondary', className)}
      aria-hidden="true"
    >
      {displayedText}
      <BlinkingCursor phase={phase} />
    </span>
  );
});
